import { z } from "zod";
// Base class for event run metadata with common display fields
export const EventRunMetadataBaseSchema = z.object({
  displayName: z.string(),
});

export type EventRunMetadataBase = z.infer<typeof EventRunMetadataBaseSchema>;

// What every run against a parent session records, whichever entity it hangs
// off — mirrors the backend's `ParentSessionEventRunMetadata`, so only the
// parent's identity differs between the variants below.
const ParentSessionEventRunMetadataSchema = EventRunMetadataBaseSchema.extend({
  sessionId: z.string(),
  // Whether the caller asked for specific columns / named specific samples.
  // Stamped by the backend at dispatch time; null/absent on rows written
  // before the flags existed (see `stopRequiresConfirmation`).
  columnScoped: z.boolean().nullable().optional(),
  sampleScoped: z.boolean().nullable().optional(),
});

// Metadata for event runs associated with a control session
export const ControlSessionEventRunMetadataSchema =
  ParentSessionEventRunMetadataSchema.extend({
    controlSessionId: z.string().uuid(),
    controlSessionName: z.string().nullable().optional(),
    controlId: z.string().uuid().nullable().optional(),
    controlName: z.string().nullable().optional().nullable().optional(),
  });

export type ControlSessionEventRunMetadata = z.infer<
  typeof ControlSessionEventRunMetadataSchema
>;

// Metadata for event runs associated with a work step session — the sibling
// of the control variant (backend `WorkStepSessionEventRunMetadata`).
export const WorkStepSessionEventRunMetadataSchema =
  ParentSessionEventRunMetadataSchema.extend({
    workStepSessionId: z.string().uuid(),
    workStepSessionName: z.string().nullable().optional(),
    workStepId: z.string().uuid().nullable().optional(),
    workStepName: z.string().nullable().optional(),
  });

export type WorkStepSessionEventRunMetadata = z.infer<
  typeof WorkStepSessionEventRunMetadataSchema
>;

// Union type for all event run metadata. The base arm goes last: it accepts
// any payload with a display name and strips the rest, so a parent-specific
// arm listed after it would never match.
export const EventRunMetadataSchema = z.union([
  ControlSessionEventRunMetadataSchema,
  WorkStepSessionEventRunMetadataSchema,
  EventRunMetadataBaseSchema,
]);

export type EventRunMetadata = z.infer<typeof EventRunMetadataSchema>;

export const EventRunSchema = z.object({
  id: z.string().uuid(),
  userId: z.string(),
  sessionId: z.string(),
  workspaceId: z.string(),
  // "unknown" is an FE-only sentinel, never sent by the backend — the
  // `.catch` coerces a state value this build doesn't know so one new state
  // degrades that row, not the whole array parse (MS-3366).
  state: z
    .enum([
      "pending",
      "processing",
      "completed",
      "failed",
      "cancelled",
      "unknown",
    ])
    .catch("unknown"),
  runMetadata: EventRunMetadataSchema.nullable().optional(),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
});

export type EventRun = z.infer<typeof EventRunSchema>;

// Display name stamped on workpaper-scope runs' metadata — the runs that
// affect every sample in the session. Only a FALLBACK gate for the
// confirm-before-stop dialog: the display name is derived by two different
// backend modules with different rules, so `stopRequiresConfirmation` prefers
// the explicit scope flags and only falls back to this string for rows that
// predate them.
export const WORKPAPER_RUN_DISPLAY_NAME = "Workpaper Run";

/**
 * Whether stopping the run carrying `metadata` needs the confirm dialog.
 * True only for a full workpaper regeneration — a run scoped to neither
 * specific columns nor specific samples. Stopping any run reverts every cell
 * it generated, finished samples included, but a column or sample rerun is
 * something the user just deliberately narrowed, so undoing it is treated
 * as routine and stops immediately; only the whole-workpaper case, where a
 * stop reverts every cell in the session, gets the confirm. The backend's
 * `columnScoped`/`sampleScoped` flags are authoritative; rows written before
 * the flags existed carry neither, and fall back to the stamped display
 * name.
 */
export const stopRequiresConfirmation = (
  metadata: EventRunMetadata | null | undefined,
): boolean => {
  if (!metadata) return false;
  const columnScoped =
    "columnScoped" in metadata ? (metadata.columnScoped ?? null) : null;
  const sampleScoped =
    "sampleScoped" in metadata ? (metadata.sampleScoped ?? null) : null;
  if (columnScoped !== null || sampleScoped !== null) {
    return !columnScoped && !sampleScoped;
  }
  return metadata.displayName === WORKPAPER_RUN_DISPLAY_NAME;
};
